import { Component, inject, OnInit, signal, computed, TemplateRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { Evaluation, EvaluationConfig, EvaluationInscription, EvaluationInscriptionCritereNotation } from '@app/models/enseignant';
import { BaseConfigService, PermissionService, PlatformService, SnackbarService, UserService } from '@app/services';
import { EnseignantService } from '@app/services/enseignant.service';

@Component({
  selector: 'app-edit-evaluation',
  templateUrl: './edit-evaluation.component.html',
  styleUrl: './edit-evaluation.component.scss',
  standalone: false
})
export class EditEvaluationComponent implements OnInit {

  enseignantService = inject(EnseignantService);
  userService = inject(UserService);
  route = inject(ActivatedRoute);
  permService = inject(PermissionService);
  snackbarService = inject(SnackbarService);
  matDialog = inject(MatDialog);
  platformService = inject(PlatformService);
  configService = inject(BaseConfigService);

  idEnseignant: number;
  idEvaluation: number;
  evaluation: Evaluation;
  config: EvaluationConfig;
  // permToCreate: boolean;
  evaluationInscriptions = signal<EvaluationInscription[]>([]);
  evaluationInscriptionsSelected = computed(() =>
    this.evaluationInscriptions().filter(insc => insc.selected())
  );

  saving: boolean = false;
  dialogEvaluationData = signal<{ loading: boolean; result?: any }>({ loading: true });

  ngOnInit(): void {
    this.idEnseignant = this.userService.currentUser.idEnseignant;
    // this.permToCreate = this.permService.hasPermission('enseignant_evaluation_create');
    this.idEvaluation = +this.route.snapshot.paramMap.get('id') !== 0 ? +this.route.snapshot.paramMap.get('id') : null;

    this.enseignantService.getEvaluationConfig().subscribe(conf => this.config = conf);
    this.enseignantService.getEvaluation(this.idEvaluation, this.idEnseignant).subscribe(evaluation => {

      const criteresMap = new Map<number, string>();
      evaluation?.criteresNotation?.forEach(c => {
        criteresMap.set(c.id, c.type);
      });

      this.evaluation = evaluation;
      this.evaluationInscriptions.set(
        this.initInscriptions(evaluation).sort((a, b) =>
          a.nomReferent.toLowerCase().localeCompare(b.nomReferent.toLowerCase())
        )
      );

      this.evaluationInscriptions().forEach(inscription => {
        inscription.criteresData = {};
        inscription._TabBOEvaluationInscriptionCritereNotation ??= [];
        inscription.appreciation = this.decodeHtml(inscription.appreciation); // En attendant de voir ce qui peut être fait côté back


        this.evaluation.criteresNotation?.forEach(critere => {
          if (!(critere.id in inscription.criteresData)) {
            inscription.criteresData[critere.id] = this.enseignantService.getValeurInitialePourCritere(critere); // ex: 0, false, null
          }
        });
      });

      this.initCriteresData(this.evaluationInscriptions(), criteresMap);
      this.calculerNotesGlobales();
    });
  }

  decodeHtml(value: string): string {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = value || '';
    return textarea.value;
  }

  initInscriptions(evaluation: Evaluation): EvaluationInscription[] {
    if (!evaluation.allIInscriptions) return [];

    const selectedMap = new Map<number, EvaluationInscription>(
      (evaluation.inscriptionsSelected ?? []).map(insc => [insc.idInscription, insc])
    );

    return evaluation.allIInscriptions.map(insc => {
      const selectedInscription = selectedMap.get(insc.idInscription);
      if (selectedInscription) {
        return { ...selectedInscription, selected: signal(true) };
      } else {
        return { ...insc, selected: signal(false) };
      }
    });
  }

  initCriteresData(inscriptions: EvaluationInscription[], criteresMap: Map<number, string>): void {
    inscriptions.forEach(inscription => {
      const reponsesInscription = inscription._TabBOEvaluationInscriptionCritereNotation;
      inscription.criteresData = {};

      reponsesInscription.forEach(rep => {
        const id = rep.idCritereNotation;
        const type = criteresMap.get(id);

        inscription.criteresData[id] =
          rep.idCritereNotationValeur && rep.idCritereNotationValeur !== 0
            ? rep.idCritereNotationValeur
            : this.enseignantService.parseValeurCritereNotation(rep.valeur, type);
      });
    });
  }

  formatCriteresData(inscription: EvaluationInscription): EvaluationInscriptionCritereNotation[] {
    const formatData: EvaluationInscriptionCritereNotation[] = [];
    const idEvaluationInscription = inscription.idEvaluationInscription;

    if (this.evaluation.criteresNotation) {
      const criteresMap = new Map<number, string>();
      this.evaluation.criteresNotation.forEach(c => criteresMap.set(c.id, c.type));

      Object.entries(inscription.criteresData || {}).forEach(([idCritere, data]) => {
        const idCritereNotation = +idCritere;
        const type = criteresMap.get(idCritereNotation);

        const isListe = type === 'liste';
        const isCheckbox = type === 'checkbox';

        formatData.push({
          idEvaluationInscription,
          idCritereNotation,
          valeur: isListe ? '' : isCheckbox ? (data ? '1' : '0') : String(data), // texte, nombre, booléen converti
          idCritereNotationValeur: isListe ? +data : 0
        });
      });
    }

    return formatData;
  }

  calculerNotesGlobales() {
    this.enseignantService.calculerNoteGlobale(this.evaluationInscriptions(), this.evaluation);
  }

  recalculerNoteGlobale(index: number) {
    this.enseignantService.calculerNoteGlobale(this.evaluationInscriptions()[index], this.evaluation);
  }

  actualiserReussite(index?: number) {
    if (typeof index === 'number' && this.evaluationInscriptions()[index]) {
      this.enseignantService.actualiserReussite(this.evaluationInscriptions()[index], this.evaluation);
    } else {
      this.enseignantService.actualiserReussite(this.evaluationInscriptions(), this.evaluation);
    }
  }

  openDialogEvaluationsByInscription(templateRef: TemplateRef<any>, inscription: EvaluationInscription): void {
    this.dialogEvaluationData.set({ loading: true }); // Réinitialise l'état avant l'ouverture

    const refDialog = this.matDialog.open(templateRef, {
      data: { evalInscrData: this.dialogEvaluationData, nomReferent: inscription.nomReferent, idEvaluation: inscription.idEvaluation }
    });

    this.enseignantService
      .getEvaluationsInscriptionsByIdInscription(this.idEnseignant, inscription.idInscription)
      .subscribe((result: any[]) => {

        const filteredResult = result.filter(
          res => res.evaluation.idEvaluation !== this.evaluation.idEvaluation
        );

        filteredResult.forEach(res => {
          let evaluation: Evaluation = res.evaluation;
          let evaluationInscription: EvaluationInscription = res.evaluationInscription;

          const criteresMap = new Map<number, string>();
          evaluation?.criteresNotation?.forEach(c => {
            criteresMap.set(c.id, c.type);
          });

          evaluationInscription.criteresData = {};
          evaluationInscription._TabBOEvaluationInscriptionCritereNotation ??= [];

          evaluation.criteresNotation?.forEach(critere => {
            if (!(critere.id in inscription.criteresData)) {
              evaluationInscription.criteresData[critere.id] = this.enseignantService.getValeurInitialePourCritere(critere);
            }
          });
          this.initCriteresData([evaluationInscription], criteresMap);
          this.enseignantService.calculerNoteGlobale(evaluationInscription, evaluation);
        })

        this.dialogEvaluationData.set({ loading: false, result: filteredResult });
      });


    this.platformService.adaptDialogToScreen(refDialog);
  }

  saveEvaluation(evaluation: Evaluation) {

    if (!this.evaluationInscriptions().length) return;

    this.saving = true;

    evaluation.inscriptionsSelected = this.evaluationInscriptionsSelected();
    evaluation.inscriptionsSelected.forEach(inscription => {
      inscription._TabBOEvaluationInscriptionCritereNotation = this.formatCriteresData(inscription);
    })

    this.enseignantService.saveEvaluation(evaluation, this.idEnseignant).subscribe({
      next: () => {
        this.snackbarService.info('Évaluation enregistrée avec succès');
        this.saving = false;
      },
      error: () => {
        this.snackbarService.error('Erreur lors de l\'enregistrement de l\'évaluation');
        this.saving = false;
      }
    });
  }
}
